import React from 'react';

// =============================================
// FIXEX ASSETS SECTION COMPONENT
// =============================================

export function FixedAssetsSection({
    fixedAssets, setFixedAssets,
    toBase, fmtBase,
    baseCurrencyLabel,
    fixedAssetTotals,
    lkrRate,
}) {
    const [editingAsset, setEditingAsset] = React.useState(null);
    const [faForm, setFaForm] = React.useState({});

    const typeEmoji = (type) => type === 'land' ? '🌍' : type === 'house' ? '🏠' : '🏗️';
    const typeLabel = (type) => type === 'land' ? 'Land' : type === 'house' ? 'House' : 'Building';
    const typeColor = (type) => type === 'land' ? '#10B981' : type === 'house' ? '#fbbf24' : '#60A5FA';

    const openAdd = () => {
        setFaForm({ name: '', type: 'house', location: '', area: '', builtYear: '', cmvLKR: '', fsvLKR: '', lastValuationDate: '', notes: '' });
        setEditingAsset('new');
    };

    const openEdit = (fa) => {
        setFaForm({ ...fa, cmvLKR: fa.cmvLKR ?? fa.valuationLKR ?? '', fsvLKR: fa.fsvLKR ?? '' });
        setEditingAsset(fa);
    };

    const handleSave = () => {
        const cmv = parseFloat(faForm.cmvLKR) || 0;
        const fsv = parseFloat(faForm.fsvLKR) || 0;
        const asset = {
            ...faForm,
            cmvLKR: cmv,
            fsvLKR: fsv,
            valuationLKR: cmv,
        };
        if (editingAsset === 'new') {
            setFixedAssets(prev => [...prev, { ...asset, id: Date.now() }]);
        } else {
            setFixedAssets(prev => prev.map(x => x.id === editingAsset.id ? { ...x, ...asset } : x));
        }
        setEditingAsset(null);
    };

    const handleDelete = (fa) => {
        if (confirm(`Delete "${fa.name}"? This cannot be undone.`)) {
            setFixedAssets(prev => prev.filter(x => x.id !== fa.id));
            setEditingAsset(null);
        }
    };

    const totalCMV = fixedAssets.reduce((s, f) => s + (f.cmvLKR || f.valuationLKR || 0), 0);
    const totalFSV = fixedAssets.reduce((s, f) => s + (f.fsvLKR || 0), 0);
    const totalCMVBase = fixedAssets.reduce((s, f) => s + toBase(f.cmvLKR || f.valuationLKR || 0, 'LKR'), 0);
    const totalFSVBase = fixedAssets.reduce((s, f) => s + toBase(f.fsvLKR || 0, 'LKR'), 0);
    const equityPremium = totalCMV - totalFSV;

    return (
        <div style={{ paddingBottom: '100px' }}>
            {/* ── Add/Edit Modal ─────────────────────────────── */}
            {editingAsset !== null && (
                <div className="modal-overlay" onClick={() => setEditingAsset(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-title">
                                {editingAsset === 'new' ? '+ Add Fixed Asset' : `Edit Asset`}
                            </div>
                            <button className="close-btn" onClick={() => setEditingAsset(null)}>×</button>
                        </div>

                        {/* Type picker */}
                        <div className="form-group">
                            <label className="form-label">Asset Type</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {[
                                    { v: 'land', label: '🌍 Land' },
                                    { v: 'house', label: '🏠 House' },
                                    { v: 'building', label: '🏗️ Building' },
                                ].map(opt => (
                                    <button key={opt.v}
                                        onClick={() => setFaForm(f => ({ ...f, type: opt.v }))}
                                        style={{
                                            flex: 1, padding: '10px 6px', borderRadius: '12px',
                                            border: faForm.type === opt.v ? '2px solid var(--primary)' : '1px solid rgba(255,255,255,0.1)',
                                            background: faForm.type === opt.v ? 'rgba(255,215,0,0.12)' : 'rgba(255,255,255,0.04)',
                                            color: faForm.type === opt.v ? 'var(--primary)' : 'var(--text-secondary)',
                                            fontFamily: 'inherit', fontSize: '13px', fontWeight: faForm.type === opt.v ? '700' : '500',
                                            cursor: 'pointer', transition: 'all 0.15s',
                                        }}
                                    >{opt.label}</button>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Asset Name *</label>
                            <input type="text" className="form-input" value={faForm.name || ''}
                                onChange={e => setFaForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Land — Kadawatha (20 perch)" />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Location</label>
                            <input type="text" className="form-input" value={faForm.location || ''}
                                onChange={e => setFaForm(f => ({ ...f, location: e.target.value }))}
                                placeholder="e.g. Kadawatha, Sri Lanka" />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">{faForm.type === 'land' ? 'Land Area' : 'Built Area'}</label>
                                <input type="text" className="form-input" value={faForm.area || ''}
                                    onChange={e => setFaForm(f => ({ ...f, area: e.target.value }))}
                                    placeholder={faForm.type === 'land' ? 'e.g. 20 perch' : 'e.g. 1100 sq ft'} />
                            </div>
                            {faForm.type !== 'land' && (
                                <div className="form-group">
                                    <label className="form-label">Year Built</label>
                                    <input type="text" className="form-input" value={faForm.builtYear || ''}
                                        onChange={e => setFaForm(f => ({ ...f, builtYear: e.target.value }))}
                                        placeholder="e.g. 2025" />
                                </div>
                            )}
                        </div>

                        {/* Valuation fields */}
                        <div style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                💰 Valuation (LKR)
                            </div>
                            <div className="form-group">
                                <label className="form-label">CMV — Current Market Value (Intrinsic) ₨</label>
                                <input type="number" className="form-input" value={faForm.cmvLKR ?? ''}
                                    onChange={e => setFaForm(f => ({ ...f, cmvLKR: e.target.value }))}
                                    placeholder="e.g. 22000000" inputMode="numeric" />
                                {parseFloat(faForm.cmvLKR) > 0 && (
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '5px' }}>
                                        ≈ {fmtBase(toBase(parseFloat(faForm.cmvLKR), 'LKR'))} {baseCurrencyLabel}
                                    </div>
                                )}
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">FSV — Forced Sale Value (Realistic Exit) ₨</label>
                                <input type="number" className="form-input" value={faForm.fsvLKR ?? ''}
                                    onChange={e => setFaForm(f => ({ ...f, fsvLKR: e.target.value }))}
                                    placeholder="e.g. 19000000" inputMode="numeric" />
                                {parseFloat(faForm.fsvLKR) > 0 && (
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '5px' }}>
                                        ≈ {fmtBase(toBase(parseFloat(faForm.fsvLKR), 'LKR'))} {baseCurrencyLabel}
                                        {parseFloat(faForm.cmvLKR) > 0 && (
                                            <span style={{ color: '#EF4444', marginLeft: '8px' }}>
                                                (−₨{(parseFloat(faForm.cmvLKR) - parseFloat(faForm.fsvLKR)).toLocaleString()} equity premium)
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Valuation Date</label>
                            <input type="text" className="form-input" value={faForm.lastValuationDate || ''}
                                onChange={e => setFaForm(f => ({ ...f, lastValuationDate: e.target.value }))}
                                placeholder="e.g. May 2026" />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Notes</label>
                            <input type="text" className="form-input" value={faForm.notes || ''}
                                onChange={e => setFaForm(f => ({ ...f, notes: e.target.value }))}
                                placeholder="Construction details, rental potential, etc." />
                        </div>

                        <button className="btn btn-primary" onClick={handleSave}
                            disabled={!faForm.name?.trim()}>
                            {editingAsset === 'new' ? 'Add Asset' : 'Save Changes'}
                        </button>
                        <button className="btn btn-secondary" onClick={() => setEditingAsset(null)} style={{ marginTop: '8px' }}>Cancel</button>
                        {editingAsset !== 'new' && (
                            <button className="btn btn-danger" onClick={() => handleDelete(editingAsset)}>
                                🗑️ Delete Asset
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ── Section header ─────────────────────────────── */}
            <div className="section-container">
                <div className="section-header">
                    <div>
                        <div className="section-title">🏠 Fixed Assets</div>
                        <div className="section-subtitle">{fixedAssets.length} assets · Kadawatha, Sri Lanka</div>
                    </div>
                    <button className="add-btn" onClick={openAdd}>+ Add</button>
                </div>

                {/* ── Portfolio summary card ──────────────────── */}
                <div className="summary-card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(251,191,36,0.04) 100%)', border: '1px solid rgba(251,191,36,0.3)' }}>
                    <div className="summary-card-title" style={{ color: '#fbbf24' }}>Total Portfolio Value</div>

                    {/* CMV hero */}
                    <div style={{ marginTop: '10px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Market Value (CMV)</div>
                        <div style={{ fontSize: '30px', fontWeight: '800', color: '#fbbf24', letterSpacing: '-1px', marginTop: '2px' }}>
                            {fmtBase(totalCMVBase)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '1px' }}>
                            ₨{totalCMV.toLocaleString()} LKR
                        </div>
                    </div>

                    {/* FSV comparison */}
                    {totalFSV > 0 && (
                        <div style={{ marginTop: '14px', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#6ee7b7' }}>{fmtBase(totalFSVBase, { compact: true })}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>FSV (Exit)</div>
                            </div>
                            <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.08)', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#EF4444' }}>−₨{equityPremium.toLocaleString()}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>Equity Premium</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#fbbf24' }}>{fixedAssets.length}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>Assets</div>
                            </div>
                        </div>
                    )}

                    {/* Land vs Property split */}
                    <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#10B981' }}>{fmtBase(fixedAssetTotals.landBase, { compact: true })}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>🌍 Land</div>
                        </div>
                        <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#fbbf24' }}>{fmtBase(fixedAssetTotals.propertyBase, { compact: true })}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>🏠 Property</div>
                        </div>
                    </div>

                    <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--text-tertiary)', lineHeight: '1.5' }}>
                        CMV used for net worth. FSV = realistic sale price. Equity premium = illiquidity discount.
                    </div>
                </div>

                {/* ── Asset cards ─────────────────────────────── */}
                {fixedAssets.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon">🏠</div>
                        <div className="empty-text">No fixed assets — tap + Add to get started</div>
                    </div>
                )}

                {fixedAssets.map((fa) => {
                    const cmv = fa.cmvLKR || fa.valuationLKR || 0;
                    const fsv = fa.fsvLKR || 0;
                    const premium = cmv - fsv;
                    const cmvBase = toBase(cmv, 'LKR');
                    const fsvBase = toBase(fsv, 'LKR');
                    const col = typeColor(fa.type);

                    return (
                        <div key={fa.id} className="item-card"
                            style={{ borderLeft: `4px solid ${col}`, cursor: 'pointer' }}
                            onClick={() => openEdit(fa)}
                        >
                            {/* Card header */}
                            <div className="card-header">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="card-title">{fa.name}</div>
                                    <div className="card-subtitle" style={{ whiteSpace: 'normal', overflow: 'visible' }}>
                                        {fa.location}
                                        {fa.area ? ` · ${fa.area}` : ''}
                                        {fa.builtYear ? ` · Built ${fa.builtYear}` : ''}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                                    <div className="card-badge" style={{ background: `${col}18`, color: col, borderColor: `${col}30` }}>
                                        {typeEmoji(fa.type)} {typeLabel(fa.type)}
                                    </div>
                                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                                        {fa.lastValuationDate || ''}
                                    </div>
                                </div>
                            </div>

                            {/* Valuation grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                                {/* CMV */}
                                <div style={{ background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.18)', borderRadius: '10px', padding: '10px 12px' }}>
                                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>CMV (Intrinsic)</div>
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#fbbf24' }}>
                                        {cmv > 0 ? fmtBase(cmvBase) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                                    </div>
                                    {cmv > 0 && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>₨{cmv.toLocaleString()}</div>}
                                </div>
                                {/* FSV */}
                                <div style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: '10px', padding: '10px 12px' }}>
                                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>FSV (Exit Price)</div>
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#6ee7b7' }}>
                                        {fsv > 0 ? fmtBase(fsvBase) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                                    </div>
                                    {fsv > 0 && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>₨{fsv.toLocaleString()}</div>}
                                </div>
                            </div>

                            {/* Equity premium bar */}
                            {cmv > 0 && fsv > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '5px' }}>
                                        <span>FSV realisable</span>
                                        <span style={{ color: '#EF4444' }}>−₨{premium.toLocaleString()} equity premium (illiquidity discount)</span>
                                    </div>
                                    <div style={{ height: '5px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${Math.round((fsv / cmv) * 100)}%`, background: 'linear-gradient(90deg, #10B981, #6ee7b7)', borderRadius: '3px' }} />
                                    </div>
                                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '3px', textAlign: 'right' }}>
                                        {Math.round((fsv / cmv) * 100)}% realisable
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            {fa.notes && (
                                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontStyle: 'italic', lineHeight: '1.5', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '10px' }}>
                                    {fa.notes}
                                </div>
                            )}

                            {/* Edit hint */}
                            <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'right', opacity: 0.6 }}>
                                ✏️ Tap to edit
                            </div>
                        </div>
                    );
                })}

                {/* Valuation methodology note */}
                {fixedAssets.length > 0 && (
                    <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', padding: '12px 14px', fontSize: '12px', color: '#a5b4fc', lineHeight: '1.6' }}>
                        <strong style={{ color: '#818cf8' }}>💡 Valuation Methodology</strong><br />
                        <strong>CMV</strong> (Current Market Value) = intrinsic replacement + land equity. Used for net worth calculations.<br />
                        <strong>FSV</strong> (Forced Sale Value) = realistic liquidation within 3–6 months. Strategic planning baseline per advisor recommendation.<br />
                        Exchange rate: 1 SGD = {lkrRate} LKR · Valuated {fixedAssets[0]?.lastValuationDate || 'May 2026'}
                    </div>
                )}
            </div>
        </div>
    );
}